import { DatoClientStore } from '@src/stores-v2/DatoClientStore';
import { Speaker } from '../components/content-components/SpeakerSelector';

interface DatoSpeaker {
  id: string;
  name?: string;
  title?: string;
  voice?: string;
  bio?: string;
}

interface SpeakerVoiceMapping {
  mappings: Record<string, string>; // Maps speakerLabel to Dato speaker ID
  storyId: string;
  lastUpdated: number;
}

/**
 * Extracts unique speakers from transcript elements
 * 
 * @param elements - The transcript elements to process
 * @returns Array of unique speaker labels
 */
export function extractSpeakerLabelsFromTranscriptElements(elements: any[]): string[] {
  // Set to track unique speaker labels
  const speakerLabels = new Set<string>();
  
  // Process each element to find speaker labels
  elements.forEach(element => {
    if (element.speaker_label && element.speaker_label.trim() !== '') {
      speakerLabels.add(element.speaker_label);
    }
  });
  
  // Convert the set to an array
  return Array.from(speakerLabels);
}

/**
 * Extracts speaker name from quote with attribution format
 * 
 * @param quote - The quote text that might contain attribution
 * @returns The extracted speaker name or null if not found
 */
export function extractSpeakerFromQuote(quote: string): string | null {
  if (!quote || typeof quote !== 'string') return null;
  
  // Match pattern: any text followed by " --- " and then the speaker name
  const attributionRegex = /^(.*?)(?:\s+---\s+)(.+)$/;
  const match = quote.trim().match(attributionRegex);
  
  if (match && match[2]) {
    // Trim any extra quote characters that might be present in the speaker name
    return match[2].trim().replace(/["']/g, '');
  }
  
  return null;
}

/**
 * Cleans quote text by removing attribution if present
 * 
 * @param quote - The quote text that might contain attribution
 * @returns The cleaned quote text
 */
export function cleanQuoteText(quote: string): string {
  if (!quote || typeof quote !== 'string') return quote;
  
  // Match pattern: any text followed by " --- " and then the speaker name
  const attributionRegex = /^(.*?)(?:\s+---\s+)(.+)$/;
  const match = quote.trim().match(attributionRegex);
  
  if (match && match[1]) {
    return match[1].trim();
  }
  
  return quote.trim();
}

/**
 * Combines speaker data from transcript elements, mappings and Dato speakers
 * 
 * @param speakerLabels - The unique speaker labels from transcript
 * @param speakerMappings - Mappings from speaker labels to Dato speaker IDs
 * @param datoSpeakers - Speaker information from Dato CMS
 * @returns Array of complete speaker objects
 */
export function buildSpeakersFromMappings(
  speakerLabels: string[],
  speakerMappings: SpeakerVoiceMapping | null,
  datoSpeakers: DatoSpeaker[]
): Speaker[] {
  // Create a map of Dato speaker IDs to speaker information
  const datoSpeakerMap = datoSpeakers.reduce((map, speaker) => {
    map[speaker.id] = speaker;
    return map;
  }, {} as Record<string, DatoSpeaker>);

  
  // Build the complete speaker list
  return speakerLabels.map(speakerLabel => {
    // Find the Dato speaker ID if this label is mapped
    const datoSpeakerId = speakerMappings?.mappings?.[speakerLabel];
    
    // Find the Dato speaker information if available
    const datoSpeaker = datoSpeakerId ? datoSpeakerMap[datoSpeakerId] : undefined;
    
    // Build the speaker object
    return {
      speakerLabel,
      name: datoSpeaker?.name || null,
      title: datoSpeaker?.title || null,
      voice: datoSpeaker?.voice || null,
      bio: datoSpeaker?.bio || null,
      isAnonymous: !datoSpeaker?.name
    };
  }).filter(speaker => !speaker.isAnonymous);
}

/**
 * Filters transcript elements to include only those from selected speakers
 * 
 * @param elements - The transcript elements to filter
 * @param selectedSpeakerLabels - Array of speaker labels to include
 * @returns Filtered transcript elements
 */
export function filterElementsBySpeakers(
  elements: any[],
  selectedSpeakerLabels: string[]
): any[] {
  // If no speakers selected or empty selection, return all elements
  if (!selectedSpeakerLabels || selectedSpeakerLabels.length === 0) {
    return elements;
  }
  
  // Filter elements to include only those from selected speakers
  return elements.filter(element => 
    element.speaker_label && selectedSpeakerLabels.includes(element.speaker_label)
  );
}

/**
 * Formats transcript with speaker information for better prompts
 * 
 * @param elements - The transcript elements to format
 * @param speakers - Information about speakers
 * @returns Formatted transcript text
 */
export function formatTranscriptWithSpeakers(
  elements: any[],
  speakers: Speaker[]
): string {
  if (!elements || elements.length === 0) {
    return "";
  }
  
  // Create a speaker map for easy lookup
  const speakerMap = speakers.reduce((map, speaker) => {
    map[speaker.speakerLabel] = speaker;
    return map;
  }, {} as Record<string, Speaker>);
  
  // Group elements by speaker
  const speakerGroups: Record<string, string[]> = {};
  
  elements.forEach(element => {
    const speakerLabel = element.speaker_label || 'unknown';
    if (!speakerGroups[speakerLabel]) {
      speakerGroups[speakerLabel] = [];
    }
    
    if (element.value) {
      speakerGroups[speakerLabel].push(element.value);
    }
  });
  
  // Format each speaker's text
  const formattedSpeakers = Object.entries(speakerGroups).map(([speakerLabel, texts]) => {
    const speaker = speakerMap[speakerLabel] || {
      speakerLabel,
      isAnonymous: true,
      name: null
    };
    
    let speakerPrefix = "";
    if (speaker.isAnonymous) {
      speakerPrefix = `Speaker ${speaker.speakerLabel}: `;
    } else if (speaker.name && speaker.title) {
      speakerPrefix = `${speaker.name} (${speaker.title}): `;
    } else if (speaker.name) {
      speakerPrefix = `${speaker.name}: `;
    }
    
    return `${speakerPrefix}${texts.join(' ')}`;
  });
  
  return formattedSpeakers.join('\n\n');
}

/**
 * Creates a transcript text filtered by selected speakers with proper attribution
 * 
 * @param elements - The original transcript elements
 * @param selectedSpeakers - Array of selected speakers to filter by
 * @returns Object containing filtered elements and formatted text
 */
export function createSpeakerFilteredTranscript(
  elements: any[],
  selectedSpeakers: Speaker[]
): { elements: any[], text: string } {
  if (!elements || elements.length === 0 || !selectedSpeakers || selectedSpeakers.length === 0) {
    return { 
      elements,
      text: elements.filter(el => el.value && el.state !== 'removed')
        .map(el => el.value)
        .join(' ')
    };
  }
  
  // Extract selected speaker labels for filtering
  const selectedSpeakerLabels = selectedSpeakers.map(speaker => speaker.speakerLabel);
  
  // Filter elements to include only those from selected speakers
  const filteredElements = filterElementsBySpeakers(elements, selectedSpeakerLabels);
  
  // If filtering resulted in empty array, return original elements
  if (filteredElements.length === 0) {
    console.warn('Speaker filtering resulted in empty transcript, using complete transcript instead');
    return { 
      elements,
      text: elements.filter(el => el.value && el.state !== 'removed')
        .map(el => el.value)
        .join(' ')
    };
  }
  
  // Create a map of speaker labels to names for adding to the text
  const speakerNameMap = selectedSpeakers.reduce((map, speaker) => {
    map[speaker.speakerLabel] = speaker.name || `Speaker ${speaker.speakerLabel.replace('spk_', '')}`;
    return map;
  }, {} as Record<string, string>);
  
  // Group elements by speaker to format them properly
  const speakerGroups: Record<string, string[]> = {};
  
  filteredElements.forEach(el => {
    if (el.value && typeof el.value === 'string' && el.state !== 'removed') {
      const speakerLabel = el.speaker_label || 'unknown';
      if (!speakerGroups[speakerLabel]) {
        speakerGroups[speakerLabel] = [];
      }
      speakerGroups[speakerLabel].push(el.value);
    }
  });
  
  // Format each speaker's text with proper speaker attribution
  const formattedSpeakerTexts = Object.entries(speakerGroups).map(([speakerLabel, texts]) => {
    const speakerName = speakerNameMap[speakerLabel] || `Speaker ${speakerLabel.replace('spk_', '')}`;
    return `${speakerName}: ${texts.join(' ')}`;
  });
  
  // Join speaker segments with double line breaks
  const formattedText = formattedSpeakerTexts.join('\n\n');
  
  console.log(`Filtered transcript for speakers: ${selectedSpeakerLabels.join(', ')}`);
  console.log(`Filtered from ${elements.length} to ${filteredElements.length} elements`);
  
  return {
    elements: filteredElements,
    text: formattedText
  };
}

/**
 * Checks if transcript elements have speaker data
 * 
 * @param elements - The transcript elements to check
 * @returns Boolean indicating if speaker data exists
 */
export function hasSpeakerData(elements: any[]): boolean {
  if (!elements) return false;
  return elements.some(element => element.speaker_label);
}

/**
 * Fetches speaker voice mappings from API
 * 
 * @param storyId - ID of the story to fetch mappings for
 * @returns Speaker voice mappings or null if unavailable
 */
export async function fetchSpeakerVoiceMappings(
  storyId: string
): Promise<SpeakerVoiceMapping | null> {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/story/${storyId}/transcript-data?resource=speaker-mappings&storyId=${storyId}`;
    const response = await fetch(url);
    if (!response.ok) {
      console.error('Failed to fetch speaker mappings');
      return null;
    }
    
    const data = await response.json();
    return data.data || null;
  } catch (error) {
    console.error('Error fetching speaker mappings:', error);
    return null;
  }
}

/**
 * Fetches story speakers from Dato CMS
 * 
 * @param storyId - ID of the story to fetch speakers for
 * @returns Array of Dato speakers
 */
export async function fetchDatoSpeakers(
  storyId: string,
  datoClientStore: DatoClientStore
): Promise<DatoSpeaker[]> {
  try {
    const datoSpeakers = await datoClientStore.storyRepository.getStorySpeakers(storyId);
    return datoSpeakers;
  } catch (error) {
    console.error('Error fetching Dato speakers:', error);
    return [];
  }
}

/**
 * Enhances a prompt with speaker context when filtering by speaker
 * 
 * @param prompt - The original prompt
 * @param selectedSpeakers - Array of selected speakers
 * @param allSpeakers - Array of all available speakers
 * @returns Enhanced prompt with speaker context
 */
export function enhancePromptWithSpeakerContext(
  prompt: string,
  selectedSpeakers: Speaker[],
  allSpeakers: Speaker[]
): string {
  // If all speakers are selected or empty selection, return original prompt
  if (selectedSpeakers.length === allSpeakers.length || selectedSpeakers.length === 0) {
    return prompt;
  }
  
  // Get names of selected speakers for the prompt
  const speakerNames = selectedSpeakers.map(speaker => {
    if (speaker.isAnonymous) {
      return `Speaker ${speaker.speakerLabel}`;
    } else {
      return speaker.name || `Speaker ${speaker.speakerLabel}`;
    }
  }).join(", ");
  
  // Enhance the prompt with speaker context
  return `Based ONLY on the words of ${speakerNames}, ${prompt}`;
}

/**
 * Gets a readable speaker name from a speaker label
 * @param speakerLabel The anonymous speaker label (e.g., "spk_0") or null/undefined
 * @returns A formatted speaker name
 */
export function getSpeakerNameFromLabel(speakerLabel: string | null | undefined): string {
  if (!speakerLabel) return 'Unknown';
  
  // Format anonymous speaker labels
  if (speakerLabel.startsWith('spk_')) {
    return `Speaker ${parseInt(speakerLabel.replace('spk_', '')) + 1}`;
  }
  
  // If it's not an anonymous label, return as is
  return speakerLabel;
}